import { createContext, useContext, type ReactNode } from 'react';

import type { StaffProfileStoresItem } from '@nucleus-commerce/api-client/admin';
import { FormatProvider } from '@nucleus-commerce/i18n/react';

/** The store as /me lists it: id, slug, name, status, default currency, locale and timezone. */
export type Store = StaffProfileStoresItem;

const StoreContext = createContext<Store | null>(null);

/** Intl throws a RangeError for an unknown zone; one bad setting must not take the screen down. */
export function displayTimeZone(timezone: string | null | undefined): string {
  if (!timezone) return 'UTC';
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone });
    return timezone;
  } catch {
    return 'UTC';
  }
}

/**
 * Provides the current store (from the /s/$storeId route) to features, and formats every date
 * below it in the store's timezone (ADR 0043).
 */
export function StoreProvider({ store, children }: { store: Store; children: ReactNode }) {
  return (
    <StoreContext.Provider value={store}>
      <FormatProvider timeZone={displayTimeZone(store.timezone)}>{children}</FormatProvider>
    </StoreContext.Provider>
  );
}

export function useStore(): Store {
  const store = useContext(StoreContext);
  if (!store) throw new Error('useStore must be used inside a /s/$storeId route');
  return store;
}

/** For code that also renders outside `/s/$storeId` (the store page under the organisation). */
export function useStoreOrNull(): Store | null {
  return useContext(StoreContext);
}
